import { CloseOutlined, CrownOutlined } from "@ant-design/icons";
import {
  Switch,
  Table,
  Input,
  Row,
  TablePaginationConfig,
  notification,
} from "antd";
import { FilterValue, SorterResult } from "antd/lib/table/interface";
import { DistributionPointResponseResource, setEnabled } from "api/generated";
import { useState, useEffect, useCallback } from "react";
import gasIcon from "../../../assets/images/mini_gas_pump.svg";
import DistributionPointInfoSection from "./DistrubutionPointInfoSection";
import DistributionPointWarehousesDetail from "./DistributionPointWarehousesDetail";
import { handleAxiosError } from "common/helpers/generalUtils";
import { useIsMobile } from "hooks/useIsMobile";
import { Client } from "api/generated/client";
import { useDebounce } from "hooks/commons/usePaginationTable";

const RESERVED_HEIGHT_PX = 400;

interface DistributionPointTableProps {
  distributionPointList: DistributionPointResponseResource[];
  refreshOnUpdate: () => void;
  pagination: TablePaginationConfig;
  handleTableChange: (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter:
      | SorterResult<DistributionPointResponseResource>
      | SorterResult<DistributionPointResponseResource>[],
  ) => void;
  handleSearchChange: (value: string) => void;
  onSelectDistributionPointId: (id: string) => void;
  client: Client;
}

export function DistributionPointTable({
  distributionPointList,
  refreshOnUpdate,
  pagination,
  handleTableChange,
  handleSearchChange,
  onSelectDistributionPointId,
  client,
}: DistributionPointTableProps) {
  const [filterText, setFilterText] = useState("");
  const debouncedFilterText = useDebounce(filterText, 500);
  const isMobile = useIsMobile();
  const [tableScrollY, setTableScrollY] = useState<number>(
    window.innerHeight - RESERVED_HEIGHT_PX,
  );

  const updateTableHeight = useCallback(() => {
    setTableScrollY(Math.max(window.innerHeight - RESERVED_HEIGHT_PX, 200));
  }, []);

  useEffect(() => {
    updateTableHeight();
    window.addEventListener("resize", updateTableHeight);
    return () => window.removeEventListener("resize", updateTableHeight);
  }, [updateTableHeight]);

  useEffect(() => {
    handleSearchChange(debouncedFilterText);
  }, [debouncedFilterText]);

  function toggleIsEnabled(enabled: boolean, distributionPointId: string) {
    setEnabled({
      client,
      path: {
        id: distributionPointId,
        isEnabled: enabled,
      },
    })
      .then(() => {
        notification.success({
          message: "Actualización exitosa",
        });
        refreshOnUpdate();
      })
      .catch((err) =>
        handleAxiosError(
          err,
          "No fue posible actualizar el punto de distribucion",
        ),
      );
  }
  const columns = [
    {
      title: "",
      dataIndex: "",
      key: "icon",
      width: "5%",
      responsive: ["md"] as "md"[],
      render: () => <img src={gasIcon} alt="Punto de distribución" />,
    },
    {
      title: "Nombre",
      dataIndex: "subsidiary",
      key: "subsidiary",
      sorter: true,
      ellipsis: true,
    },
    {
      title: "Núm. Permiso",
      dataIndex: "senerLicense",
      key: "senerLicense",
      sorter: true,
      responsive: ["sm"] as "sm"[],
      ellipsis: true,
    },
    {
      title: "En Uso",
      dataIndex: "entitled",
      key: "entitled",
      width: isMobile ? "80px" : "100px",
      filters: [
        { text: "En Uso", value: true },
        { text: "Inhabilitado", value: false },
      ],
      render: (
        entitled: boolean,
        record: DistributionPointResponseResource,
      ) => (
        <Switch
          checked={entitled}
          onChange={(checked) => toggleIsEnabled(checked, record.id ?? "")}
          checkedChildren={<CrownOutlined />}
          title={entitled ? "En Uso" : "Inhabilitado"}
          unCheckedChildren={<CloseOutlined />}
        />
      ),
    },
    {
      title: "Editar",
      dataIndex: "id",
      key: "id",
      width: isMobile ? "60px" : "80px",
      render: (id: string) => (
        <DistributionPointInfoSection
          distributionPointId={id}
          onSelectDistributionPointId={onSelectDistributionPointId}
        />
      ),
    },
  ];

  return (
    <>
      <Row style={{ marginBottom: "12px", width: "100%" }}>
        <Input.Search
          placeholder="Busca por Nombre o Número de Permiso"
          enterButton
          onSearch={(value) => setFilterText(value)}
          onChange={(e) => setFilterText(e.target.value)}
          value={filterText}
          style={{ width: "100%" }}
        />
      </Row>
      <Row style={{ width: "100%" }}>
        <Table<DistributionPointResponseResource>
          columns={columns}
          expandable={{
            expandedRowRender: (record) => (
              <DistributionPointWarehousesDetail
                warehouses={record.warehouseList}
              />
            ),
            defaultExpandAllRows: true,
          }}
          dataSource={distributionPointList}
          rowKey="id"
          scroll={{ x: "max-content", y: tableScrollY }}
          pagination={pagination}
          onChange={handleTableChange}
          size={isMobile ? "small" : "middle"}
          footer={() => <div>Total: {pagination.total}</div>}
          style={{ width: "100%" }}
        />
      </Row>
    </>
  );
}

export default DistributionPointTable;
